import React from 'react';
import { tr } from '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { useRouteError, useNavigate, isRouteErrorResponse } from 'react-router-dom';
import { Image } from 'primereact/image';
import { somethingWentWrongImage, notFoundImage } from '../../assets/images';
import { AppButton, ErrorPageLayout } from '../../components';
import { isChunkLoadError } from '../../utility/lazyRetry';
import { getHomeRoute } from '../../utility/navigationHelper';

/**
 * RouteErrorBoundary — React Router v6 error element.
 *
 * Placed as `errorElement` on root routes to catch:
 * 1. **Chunk load errors** — shows an "Update Available" screen with a manual
 *    refresh. It does not auto-reload: lazyRetry already spent the single
 *    recovery reload before the error could reach this boundary.
 * 2. **404 route errors** — shows "Page Not Found" using ErrorPageLayout
 * 3. **Other runtime errors** — shows 'Something Went Wrong' with retry option
 *
 * Uses the existing ErrorPageLayout component for consistent error UI.
 */
const RouteErrorBoundary: React.FC = () => {
  const { t } = useTranslation();
  const error = useRouteError();
  const navigate = useNavigate();

  // Handle chunk load errors.
  //
  // This boundary deliberately does NOT reload. By the time a chunk error
  // reaches here, lazyRetry has already spent its single recovery reload and
  // the chunk still failed — so it is genuinely missing from the deploy, not
  // stale. Reloading again just restarts the cycle (this was one of three
  // independent reloaders that together produced an endless refresh loop).
  // Show the error screen and let the user retry explicitly instead.
  if (isChunkLoadError(error)) {
    return (
      <ErrorPageLayout
        title={t('dialog.update_available', 'Update Available')}
        code="⟳"
        message={tr(
          'g.a_new_version_of_the_application_is_available_please_re',
          'A new version of the application is available. Please refresh your browser to continue.',
        )}
        image={
          <Image
            src={somethingWentWrongImage}
            alt={tr('common.alt_update_available', 'Update available')}
            width="100%"
          />
        }
      >
        <div className="flex flex-col gap-3 items-center">
          <AppButton
            label={t('common.button_refresh_page', 'Refresh Page')}
            onClick={() => {
              window.location.href = window.location.pathname;
            }}
          />
          <AppButton
            label={t('common.button_go_to_home', 'Go to Home')}
            outlined
            onClick={() => navigate(getHomeRoute())}
          />
        </div>
      </ErrorPageLayout>
    );
  }

  // Handle 404 route errors
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <ErrorPageLayout
        title={t('dialog.page_not_found', 'Page Not Found')}
        code="404"
        message={tr(
          'g.sorry_the_page_you_are_looking_for_does_not_exist_or_ha',
          'Sorry, the page you are looking for does not exist or has been moved.',
        )}
        image={
          <Image
            src={notFoundImage}
            alt={tr('common.alt_page_not_found', 'Page not found')}
            width="100%"
          />
        }
      >
        <AppButton label={t('common.button_go_back')} onClick={() => navigate(-1)} />
      </ErrorPageLayout>
    );
  }

  // Handle other route errors (status codes)
  if (isRouteErrorResponse(error)) {
    return (
      <ErrorPageLayout
        title={t('dialog.something_went_wrong', 'Something Went Wrong')}
        code={String(error.status)}
        message={
          error.statusText ||
          tr(
            'g.an_unexpected_error_occurred_please_try_again',
            'An unexpected error occurred. Please try again.',
          )
        }
        image={<Image src={somethingWentWrongImage} alt={t('g.error', 'Error')} width="100%" />}
      >
        <div className="flex flex-col gap-3 items-center">
          <AppButton
            label={t('common.try_again', 'Try Again')}
            onClick={() => window.location.reload()}
          />
          <AppButton label={t('common.button_go_back')} outlined onClick={() => navigate(-1)} />
        </div>
      </ErrorPageLayout>
    );
  }

  // Handle generic runtime errors
  const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : (error as any)?.message ||
        tr(
          'g.an_unexpected_error_occurred_2',
          tr('g.an_unexpected_error_occurred_2', 'An unexpected error occurred.'),
        );
  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <ErrorPageLayout
      title={t('dialog.something_went_wrong', 'Something Went Wrong')}
      code="Error"
      message={
        isDev
          ? `${errorMessage}`
          : tr(
              'g.an_unexpected_error_occurred_please_try_again_or_contac',
              'An unexpected error occurred. Please try again or contact support if the problem persists.',
            )
      }
      image={
        <Image
          src={somethingWentWrongImage}
          alt={tr('common.alt_something_wrong', 'Something went wrong')}
          width="100%"
        />
      }
    >
      <div className="flex flex-col gap-3 items-center w-full max-w-2xl">
        {isDev && errorStack && (
          <pre className="bg-red-50 border border-red-200 rounded-md p-3 text-[11px] text-left overflow-auto max-h-[200px] w-full text-red-800 whitespace-pre-wrap break-all">
            {errorStack}
          </pre>
        )}
        <AppButton
          label={t('common.try_again', 'Try Again')}
          onClick={() => window.location.reload()}
        />
        <AppButton label={t('common.button_go_back')} outlined onClick={() => navigate(-1)} />
      </div>
    </ErrorPageLayout>
  );
};

export default RouteErrorBoundary;
